<template>
  <div
    :class="getItemTableContainerClass"
  >
    <ItemTable
      ref="itemTable"
      :boundItemsUrl="boundItemsUrl"
      :columns="columns"
      :handleBulkDelete="handleBulkDelete"
      :handleRowClick="showCommandResultDetails"
      :hasSelectColumn="true"
      :inDetailsAsidePanel="!inMainPanel"
      :itemType="itemType"
      :keepFresh="true"
      :selectionItemText="{
        label: 'selected',
        plural: 'command results',
        singular: 'command result',
      }"
      :showBound="false"
      :showBulkDelete="!isCommandRunner"
      :showSearchForm="true"
      :viewport="viewport"
    />
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import CommandResultsDetailsModal from 'client/Commands/CommandResultDetailsModal.vue';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import ModalEventBus from 'components/Modal/ModalEventBus';
import moment from 'moment';
import pluralize from 'pluralize';

const { ItemTableConsts, ItemTypeConsts, StoreModuleMap } = Constants;
const { columnTypes } = ItemTableConsts;
const { error, success } = Constants.default;
const commandResultsCollection = StoreModuleMap[ItemTypeConsts.commandResult];

// Legacy aside panel events.
// This can be removed when the commands aside panel has been refactored.
const legacyAsidePanelCanceledEventName = 'command-details:canceled';
const legacyAsidePanelSavedEventName = 'command-details:saved';

export default {
  name: 'CommandResultItemTable',

  components: {
    ItemTable,
  },

  mixins: [
    ItemTableComputedMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
  ],

  data() {
    return {
      columns: [
        {
          bodyComponent: 'ItemName',
          bodyComponentProps: {
            titleField: 'name',
            subtitleFunction: item => this.getCommandSubtitle(item),
          },
          classNames: {
            body: [this.$style.commandNameColumn],
            heading: [columnTypes.nameColumn],
          },
          headingText: 'Name',
          sortBy: 'name',
        },
        {
          dataField: 'exitCode',
          headingText: 'Exit Code',
          sortBy: 'exitCode',
        },
        {
          bodyComponent: 'ItemName',
          bodyComponentProps: {
            titleField: 'system',
            subtitleFunction: item => this.getTimeSubtitle(item),
          },
          classNames: {
            body: [columnTypes.nameColumn],
            heading: [columnTypes.nameColumn],
          },
          headingText: 'Device',
          sortBy: 'system',
        },
        {
          bodyComponent: 'ViewDetailsButton',
          bodyComponentProps: {
            handleClick: this.handleViewDetailsClick,
          },
          classNames: {
            body: ['viewResultsButtonColumn'],
            heading: ['viewResultsButtonColumn'],
          },
        },
      ],
      itemType: ItemTypeConsts.commandResult,
      panelType: ItemTableConsts.panelType.mainPanel,
      route: '/#/commands',
    };
  },

  computed: {
    ...mapGetters(commandResultsCollection, {
      selectedItems: 'getSelectedItems',
    }),

    ...mapGetters('SettingsModule', {
      isCommandRunner: 'isCommandRunner',
    }),
  },

  mounted() {
    if (this.viewport) {
      const { refresh } = this.$refs.itemTable;
      this.viewport.on(legacyAsidePanelCanceledEventName, refresh);
      this.viewport.on(legacyAsidePanelSavedEventName, refresh);
    }
  },

  destroyed() {
    if (this.viewport) {
      this.viewport.off(legacyAsidePanelCanceledEventName);
      this.viewport.off(legacyAsidePanelSavedEventName);
    }
  },

  methods: {
    ...mapActions(commandResultsCollection, [
      'deleteItems',
      'fetchItem',
    ]),

    getCommandSubtitle(item) {
      return `Command: ${item.command}`;
    },

    getTimeSubtitle(item) {
      const { responseTime, requestTime } = item;
      if (responseTime) return `Time: Finished on ${moment(requestTime).utc().format('YYYY-MM-DD [at] h:mm a')}`;
      if (requestTime) return `Time: Started on ${moment(requestTime).utc().format('YYYY-MM-DD [at] h:mm a')}`;
      return '';
    },

    handleBulkDelete() {
      const componentProps = {
        bodyText: 'Are you sure you would like to delete these command results?',
        continueText: 'yes, delete',
        handleContinue: () => {
          componentProps.loading = true;
          this.handleDeleteContinue();
        },
        headerText: 'Delete Command Result(s)',
        loading: false,
        mainActionAttention: true,
      };

      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps,
      });
    },

    handleDeleteContinue() {
      this.deleteItems(this.selectedItems)
        .then((result) => {
          const { deletedIds, failedTasks } = result;
          if (failedTasks.length > 0) {
            notify(error, `The selected ${pluralize(`${this.itemType}`, failedTasks.length)} could not be deleted.`);
          } else {
            notify(success, `Command ${pluralize('Result', deletedIds.length)} successfully deleted.`);
          }
        })
        .finally(() => {
          ModalEventBus.close();
        });
    },

    handleViewDetailsClick(item, e) {
      e.stopPropagation();
      this.showCommandResultDetails(item.id);
    },

    async showCommandResultDetails(itemId) {
      await this.fetchItem(itemId);
      const fullCommandResult = this.getItem()(itemId);
      ModalEventBus.open({
        isLargeModal: true,
        component: CommandResultsDetailsModal,
        componentProps: {
          command: fullCommandResult,
        },
      });
    },
  },
};
</script>

<style module>
.commandNameColumn {
  max-width: 325px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
